import React, { useState, useEffect, useMemo } from 'react';
import {
  PlugIcon,
  ZapIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  TrendingUpIcon,
  CalendarIcon,
  BoltIcon } from
'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell } from
'recharts';
import { thingsboardService } from '../services/thingsboard';
import { whToKwh } from '../utils/energyUtils';
import { EditorSensor } from './FloorPlanEditor';
import { useTranslation } from 'react-i18next';

interface SEOProps extends Omit<EditorSensor, 'roomId'> {
  roomId: string;
  roomName: string;
}

export interface TopOutletWidgetProps {
  seoSensors?: SEOProps[];
}
interface OutletData {
  name: string;
  location: string;
  usage: number;
  dailyUsage: number;
  weeklyUsage: number;
  monthlyUsage: number;
  percent: number;
  todayUsage: number;
  dailyData: {
    day: number;
    usage: number;
  }[];
}
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#0d1424] border border-white/10 rounded-lg px-2.5 py-1.5 shadow-xl">
        <p className="text-[0.65rem] text-slate-400 font-roboto">{payload[0].payload.dayLabel}</p>
        <p className="text-xs text-white font-roboto font-bold">
          {Number(payload[0].value).toFixed(1)}{' '}
          <span className="text-slate-500 text-[0.6rem]">kWh</span>
        </p>
      </div>);

  }
  return null;
};
export function TopOutletWidget({ seoSensors = [] }: TopOutletWidgetProps) {
  const { t, i18n } = useTranslation();
  const [selectedOutlet, setSelectedOutlet] = useState<OutletData | null>(null);
  const [deviceCategory, setDeviceCategory] = useState<'multitab' | 'outlet'>('multitab');
  const [topOutlets, setTopOutlets] = useState<OutletData[]>([]);
  const [timeRange, setTimeRange] = useState<'daily' | 'weekly' | 'monthly'>('daily');

  // Auto-switch to category with devices if current has none
  useEffect(() => {
    const hasMultitab = seoSensors.some(s => s.type === 'SEM' || s.name?.startsWith('SEM_') || s.label?.startsWith('SEM_'));
    const hasOutlet = seoSensors.some(s => s.type === 'SEO' || s.type === 'SER' || s.type === 'PLUG' || s.type === 'OUTLET' || s.name?.startsWith('SEO_') || s.name?.startsWith('SER_'));
    if (!hasMultitab && hasOutlet) {
      setDeviceCategory('outlet');
    }
  }, [seoSensors]);

  const filteredSensors = useMemo(() => {
    if (deviceCategory === 'multitab') {
      return seoSensors.filter(
        s => s.type === 'SEM' || s.name?.startsWith('SEM_') || s.label?.startsWith('SEM_')
      );
    } else {
      return seoSensors.filter(
        s => s.type === 'SEO' || s.type === 'SER' || s.type === 'PLUG' || s.type === 'OUTLET' ||
             s.name?.startsWith('SEO_') || s.name?.startsWith('SER_') ||
             s.label?.startsWith('SEO_') || s.label?.startsWith('SER_')
      );
    }
  }, [seoSensors, deviceCategory]);

  useEffect(() => {
    if (filteredSensors.length === 0) {
      setTopOutlets([]);
      return;
    }

    const fetchOutletData = async () => {
      try {
        const outletPromises = filteredSensors.map(async (sensor) => {
          // Fetch telemetries for all ranges
          const telemetry = await thingsboardService.getLatestTelemetry('DEVICE', sensor.id, 'daily_power,weekly_power,monthly_power');
          const attributes = await thingsboardService.getAttributes('DEVICE', sensor.id, 'primary_appliance', 'SERVER_SCOPE');

          let primaryAppliance = sensor.label; // fallback to generic name (e.g. SEO_1)
          if (attributes && attributes.length > 0) {
             const attr = attributes.find((a: any) => a.key === 'primary_appliance');
             if (attr?.value) primaryAppliance = attr.value;
          }

          const dPower = Number(telemetry['daily_power']?.[0]?.value || 0);
          const wPower = Number(telemetry['weekly_power']?.[0]?.value || 0);
          const mPower = Number(telemetry['monthly_power']?.[0]?.value || 0);

          let currentUsage = dPower;
          if (timeRange === 'weekly') currentUsage = wPower;
          else if (timeRange === 'monthly') currentUsage = mPower;

          // Fetch historical daily_power for the current month (for the detail chart)
          const today = new Date();
          const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).getTime();
          const endTs = today.getTime();
          const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
          
          let historyData: any = {};
          try {
            historyData = await thingsboardService.getTimeseries(
              'DEVICE', sensor.id, 'daily_power', startOfMonth, endTs, 86400000, 31, 'MAX'
            );
          } catch (e) {
            console.error('Failed to fetch daily_power history', e);
          }

          // Build dailyData array initialized to 0
          const dailyData = Array.from({ length: daysInMonth }, (_, i) => ({
            day: i + 1,
            usage: 0
          }));

          // Process fetched history
          if (historyData && historyData['daily_power']) {
            historyData['daily_power'].forEach((point: { ts: number, value: string }) => {
              const date = new Date(point.ts);
              const dayIndex = date.getDate() - 1; // 0-based
              if (dayIndex >= 0 && dayIndex < daysInMonth) {
                const val = whToKwh(point.value);
                dailyData[dayIndex].usage = Math.max(dailyData[dayIndex].usage, Number(val.toFixed(1)));
              }
            });
          }

          // Ensure today's usage from latest telemetry is also updated if it's higher or missing in history
          const currentDayIndex = today.getDate() - 1;
          if (currentDayIndex >= 0 && currentDayIndex < daysInMonth) {
             dailyData[currentDayIndex].usage = Math.max(dailyData[currentDayIndex].usage, Number(whToKwh(dPower).toFixed(1)));
          }

          return {
            name: primaryAppliance,
            location: sensor.roomName,
            usage: Number(whToKwh(currentUsage).toFixed(1)),
            dailyUsage: whToKwh(dPower),
            weeklyUsage: whToKwh(wPower),
            monthlyUsage: whToKwh(mPower),
            percent: 0, // will compute later
            todayUsage: Number(whToKwh(dPower).toFixed(1)), // actual usage for today
            dailyData: dailyData.map(d => ({ ...d, dayLabel: t('monthly_energy.day_label', { label: d.day }) })),
          };
        });

        const results = await Promise.all(outletPromises);
        
        // Sort by usage descending and take top 3
        const sorted = results.sort((a, b) => b.usage - a.usage).slice(0, 3);
        
        // Calculate relative percentages for the bars based on the max out of the top 3
        const maxUsage = sorted.length > 0 ? sorted[0].usage : 1;
        sorted.forEach(outlet => {
          outlet.percent = maxUsage > 0 ? (outlet.usage / maxUsage) * 100 : 0;
        });

        setTopOutlets(sorted);
        // If current selected outlet is updated, refresh it too
        if (selectedOutlet) {
          const updatedSelected = sorted.find(s => s.name === selectedOutlet.name && s.location === selectedOutlet.location);
          if (updatedSelected) setSelectedOutlet(updatedSelected);
        }
      } catch (err) {
        console.error('Failed to fetch Top Outlet Data', err);
      }
    };

    fetchOutletData();
    const intervalId = setInterval(fetchOutletData, 30000); // Poll every 30s
    return () => clearInterval(intervalId);
  }, [filteredSensors, timeRange]);

  const today = new Date();
  const currentDay = today.getDate();
  const monthLabel = new Intl.DateTimeFormat(i18n.language, { year: 'numeric', month: 'long' }).format(today);
  return (
    <div className="glass-panel rounded-2xl p-4 flex flex-col gap-3 flex-1 h-full overflow-hidden">
      <AnimatePresence mode="wait">
        {selectedOutlet ?
        <motion.div
          key="detail"
          initial={{
            opacity: 0,
            x: 30
          }}
          animate={{
            opacity: 1,
            x: 0
          }}
          exit={{
            opacity: 0,
            x: 30
          }}
          transition={{
            duration: 0.2
          }}
          className="flex flex-col gap-3 flex-1 min-h-0">

            {/* Detail Header */}
            <div className="flex items-center gap-2">
              <button
              onClick={() => setSelectedOutlet(null)}
              className="p-1 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors">

                <ArrowLeftIcon className="w-4 h-4" />
              </button>
              <PlugIcon className="w-4 h-4 text-violet-400" />
              <span className="text-[1.0rem] font-medium text-slate-300 tracking-wider">
                {selectedOutlet.name}
              </span>
              <span className="text-[0.7rem] text-slate-600 font-roboto">
                {selectedOutlet.location}
              </span>
            </div>

            {/* Today's Usage */}
            <div className="flex items-center gap-3 bg-white/5 rounded-xl px-3 py-2">
              <div className="p-1.5 rounded-lg bg-amber-500/15">
                <BoltIcon className="w-3.5 h-3.5 text-amber-400" />
              </div>
              <div className="flex-1">
                <div className="text-[0.65rem] text-slate-500 uppercase tracking-wider">
                  {t('top_outlet.today_usage')}
                </div>
                <div className="text-lg font-roboto font-bold text-white leading-tight">
                  {selectedOutlet.todayUsage}
                  <span className="text-[0.7rem] text-slate-500 ml-1">kWh</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[0.65rem] text-slate-500 uppercase tracking-wider">
                  {timeRange === 'daily' ? t('top_outlet.today_usage') : timeRange === 'weekly' ? t('top_outlet.weekly_cumulative') : t('top_outlet.monthly_cumulative')}
                </div>
                <div className="text-sm font-roboto font-bold text-amber-400 leading-tight">
                  {selectedOutlet.usage}
                  <span className="text-[0.65rem] text-slate-500 ml-1">
                    kWh
                  </span>
                </div>
              </div>
            </div>

            {/* Daily Chart */}
            <div className="flex-1 min-h-0 flex flex-col">
              <div className="flex items-center gap-1.5 mb-1.5">
                <CalendarIcon className="w-3 h-3 text-slate-500" />
                <span className="text-[0.65rem] text-slate-500 font-roboto uppercase tracking-wider">
                  {t('top_outlet.daily_usage_chart', { monthLabel })}
                </span>
              </div>
              <div className="flex-1 min-h-0">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                  data={selectedOutlet.dailyData}
                  margin={{
                    top: 4,
                    right: 2,
                    left: -20,
                    bottom: 0
                  }}>

                    <XAxis
                    dataKey="day"
                    tick={{
                      fontSize: 8,
                      fill: '#475569'
                    }}
                    axisLine={false}
                    tickLine={false}
                    interval={4} />

                    <YAxis
                    tick={{
                      fontSize: 8,
                      fill: '#475569'
                    }}
                    axisLine={false}
                    tickLine={false} />

                    <Tooltip content={<CustomTooltip />} cursor={false} />
                    <Bar dataKey="usage" radius={[2, 2, 0, 0]} maxBarSize={10} minPointSize={2}>
                      {selectedOutlet.dailyData.map((entry, index) =>
                    <Cell
                      key={`cell-${index}`}
                      fill={
                      entry.day === currentDay ?
                      '#f59e0b' :
                      entry.usage > 0 ?
                      'rgba(148, 163, 184, 0.35)' :
                      'rgba(148, 163, 184, 0.08)'
                      } />

                    )}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </motion.div> :

        <motion.div
          key="list"
          initial={{
            opacity: 0,
            x: -30
          }}
          animate={{
            opacity: 1,
            x: 0
          }}
          exit={{
            opacity: 0,
            x: -30
          }}
          transition={{
            duration: 0.2
          }}
          className="flex flex-col gap-3 flex-1">

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-slate-400">
                <PlugIcon className="w-4 h-4 text-violet-400" />
                <span className="text-[1rem] text-white font-medium uppercase tracking-wider">
                  {deviceCategory === 'multitab' ? '사용량 많은 멀티탭' : '사용량 많은 콘센트'}
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                {/* Category Selector [ 멀티탭 | 콘센트 ] */}
                <div className="flex items-center bg-white/5 rounded-lg p-0.5 border border-white/10 shadow-inner">
                  <button
                    onClick={() => setDeviceCategory('multitab')}
                    className={`px-2 py-1 rounded-md text-[0.65rem] font-bold transition-all duration-200 ${
                      deviceCategory === 'multitab'
                        ? 'bg-blue-500 text-white shadow-sm'
                        : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    멀티탭
                  </button>
                  <button
                    onClick={() => setDeviceCategory('outlet')}
                    className={`px-2 py-1 rounded-md text-[0.65rem] font-bold transition-all duration-200 ${
                      deviceCategory === 'outlet'
                        ? 'bg-violet-500 text-white shadow-sm'
                        : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    콘센트
                  </button>
                </div>

                {/* Range Selector */}
                <div className="flex items-center bg-white/5 rounded-lg p-0.5 border border-white/5">
                  {(['daily', 'weekly', 'monthly'] as const).map((range) => (
                    <button
                      key={range}
                      onClick={() => setTimeRange(range)}
                      className={`
                        px-2 py-1 rounded-md text-[0.65rem] font-medium transition-all duration-200
                        ${timeRange === range 
                          ? 'bg-violet-500/20 text-violet-400 border border-violet-500/20' 
                          : 'text-slate-500 hover:text-slate-300'}
                      `}
                    >
                      {range === 'daily' ? t('top_outlet.daily') : range === 'weekly' ? t('top_outlet.weekly') : t('top_outlet.monthly')}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2.5 flex-1 justify-center">
              {topOutlets.length === 0 && (
                <div className="text-center text-slate-500 text-xs italic">
                  해당 구역에 등록된 {deviceCategory === 'multitab' ? '멀티탭' : '콘센트'} 데이터가 없습니다.
                </div>
              )}
              {topOutlets.map((outlet, i) =>
            <div key={`${outlet.location}-${outlet.name}-${i}`} className="flex items-center gap-3">
                  {/* Rank */}
                  <div
                className={`
                    w-5 h-5 rounded-md flex items-center justify-center text-[0.7rem] font-roboto font-bold flex-shrink-0
                    ${i === 0 ? 'bg-amber-500/20 text-amber-400' : 'bg-white/5 text-slate-500'}
                  `}>

                    {i + 1}
                  </div>

                  {/* Info + Bar */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-white font-medium truncate">
                          {outlet.name}
                        </span>
                        <span className="text-[0.7rem] text-slate-600 font-roboto">
                          {outlet.location}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <ZapIcon
                      className={`w-2.5 h-2.5 ${i === 0 ? 'text-amber-400' : 'text-slate-600'}`} />

                        <span
                      className={`text-[0.9rem] font-roboto font-bold ${i === 0 ? 'text-amber-400' : 'text-slate-400'}`}>

                          {outlet.usage}
                          <span className="text-[0.7rem] text-slate-600 ml-0.5">
                            kWh
                          </span>
                        </span>
                      </div>
                    </div>
                    <div className="w-full bg-slate-800 h-1 rounded-full overflow-hidden">
                      <div
                    className={`h-full rounded-full transition-all duration-1000 ${i === 0 ? 'bg-amber-500/70' : 'bg-slate-600/50'}`}
                    style={{
                      width: `${outlet.percent}%`
                    }} />

                    </div>
                  </div>

                  {/* Detail Button */}
                  <button
                onClick={() => setSelectedOutlet(outlet)}
                className="p-1 rounded-lg hover:bg-white/10 text-slate-600 hover:text-slate-300 transition-colors flex-shrink-0">

                    <ChevronRightIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
            )}
            </div>
          </motion.div>
        }
      </AnimatePresence>
    </div>);

}